<template>
  <div class="mobile-header">
    <!-- Lottery Information -->
    <div class="mobile-lottery-info">
      <div class="mobile-flag-container">
        <img 
          v-if="lottery?.game_logo" 
          :src="getGameLogoUrl(lottery.game_logo)" 
          :alt="lottery.country + ' flag'" 
          class="mobile-flag" 
        />
      </div>
      <div class="mobile-lottery-details">
        <h1 class="mobile-lottery-name">{{ lottery?.game_name || 'Loading...' }}</h1>
        <div class="mobile-lottery-country">{{ lottery?.country || '' }}</div>
      </div>
    </div>

    <!-- Countdown Timer -->
    <div class="mobile-countdown-section">
      <div class="mobile-countdown">
        <div class="mobile-time-block">
          <div class="mobile-time">{{ String(countdown.hours).padStart(2, '0') }}</div>
          <div class="mobile-label">{{ $t('time.hours') }}</div>
        </div>
        <div class="mobile-time-block">
          <div class="mobile-time">{{ String(countdown.minutes).padStart(2, '0') }}</div>
          <div class="mobile-label">{{ $t('time.mins') }}</div>
        </div>
        <div class="mobile-time-block">
          <div class="mobile-time">{{ String(countdown.seconds).padStart(2, '0') }}</div>
          <div class="mobile-label">{{ $t('time.secs') }}</div>
        </div>
      </div>
    </div>

  </div>
</template>

<script setup lang="ts">
import { defineProps, defineEmits } from 'vue';
import { getGameLogoUrl } from '../../utils/resultParser';

interface Lottery {
  game_name?: string;
  country?: string;
  game_logo?: string;
  draw_date?: string;
  cutoff?: string;
}

interface Props {
  lottery: Lottery | null;
  countdown: {
    hours: number;
    minutes: number;
    seconds: number;
  };
}

defineProps<Props>();

defineEmits<{
  'go-back': [];
}>();

</script>

<style scoped>
.mobile-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: var(--bg-primary);
  border-bottom: 1px solid var(--border-color);
  min-height: 60px;
}

.mobile-lottery-info {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.mobile-flag-container {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  overflow: hidden;
  border: 2px solid var(--border-color);
}

.mobile-flag {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.mobile-lottery-details {
  flex: 1;
  min-width: 0;
}

.mobile-lottery-name {
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.mobile-lottery-country {
  font-size: 12px;
  color: var(--text-secondary);
  margin: 0;
  line-height: 1.2;
}

.mobile-countdown-section {
  display: flex;
  align-items: center;
  margin: 0 8px;
  flex-shrink: 0;
}

.mobile-countdown {
  display: flex;
  gap: 6px;
  align-items: center;
}

.mobile-time-block {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 28px;
}

.mobile-time {
  font-size: 18px;
  font-weight: 700;
  color: var(--accent-color);
  line-height: 1;
}

.mobile-label {
  font-size: 10px;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  line-height: 1;
  margin-top: 2px;
}


/* Responsive adjustments */
@media (max-width: 480px) {
  .mobile-header {
    padding: 0px 12px;
    min-height: 56px;
  }
  
  .mobile-lottery-name {
    font-size: 14px;
  }
  
  .mobile-lottery-country {
    font-size: 11px;
  }
  
  .mobile-flag-container {
    width: 28px;
    height: 28px;
  }
  
  .mobile-time {
    font-size: 16px;
  }
  
  .mobile-label {
    font-size: 9px;
  }
  
  .mobile-countdown {
    gap: 4px;
  }
  
  .mobile-time-block {
    min-width: 24px;
  }
  
  .mobile-countdown-section {
    margin: 0 6px;
  }
}

/* Dark mode adjustments */
@media (prefers-color-scheme: dark) {
  .mobile-header {
    background: var(--bg-primary);
    border-bottom-color: var(--border-color);
  }
  
  .mobile-flag-container {
    border-color: var(--border-color);
  }
}
</style>

<script lang="ts">
export default {
  name: "MobileHeader",
};
</script>
